import type { RequestType } from '@feedback/shared';
import { strings } from '../strings';

export interface TypeSelectorProps {
  value: RequestType;
  onChange: (type: RequestType) => void;
  /** Disables every option button — set by `Modal` while a submit is in flight. */
  disabled?: boolean;
}

/**
 * The three choices a reporter sees: Problem, Idea, Praise.
 *
 * They map onto enum values triage and the DevStride sync already use, so
 * nothing downstream changes. SUGGESTION is not offered: an idea and a
 * suggestion differ only in a label, and a reporter choosing between them is
 * guessing. It stays in the enum because admin edits and old rows carry it.
 */
export const REPORTER_TYPES: RequestType[] = ['BUG', 'FEATURE', 'FEEDBACK'];

/** Maps a programmatic `open('SUGGESTION')` onto the choice that is on screen. */
export function presentType(type: RequestType): RequestType {
  return type === 'SUGGESTION' ? 'FEATURE' : type;
}

const TYPE_LABEL_ID = 'ft-type-label';

/**
 * Segmented control for picking a `RequestType`. Implements the WAI-ARIA "radio group"
 * pattern with plain buttons (`role="radiogroup"` + one `role="radio"` per option,
 * `aria-checked` marking the selection) rather than native `<input type="radio">` — buttons
 * are easier to style as a segmented control and this pattern is exactly what the ARIA
 * Authoring Practices Guide recommends for that shape.
 *
 * The group carries a visible label, which is also its accessible name, so a
 * sighted reporter and a screen reader are told the same thing.
 */
export function TypeSelector({ value, onChange, disabled }: TypeSelectorProps) {
  return (
    <div class="ft-choice">
      <span id={TYPE_LABEL_ID} class="ft-field-label">
        {strings.type.groupLabel}
      </span>
      <div class="ft-type-selector" role="radiogroup" aria-labelledby={TYPE_LABEL_ID}>
        {REPORTER_TYPES.map((type) => {
          const selected = type === value;
          return (
            <button
              key={type}
              type="button"
              role="radio"
              aria-checked={selected}
              class={selected ? 'ft-type-option ft-type-option--selected' : 'ft-type-option'}
              onClick={() => onChange(type)}
              disabled={disabled}
            >
              {strings.type.options[type]}
            </button>
          );
        })}
      </div>
    </div>
  );
}
